<script lang="ts">
  import * as _ from "lodash";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import VerifySalesRepModal from "./VerifySalesRepModal.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import type { RetailerSalesRepContact, SalesRep } from "@shared/types/SalesRep";
  import type { SharedOrder } from "@shared/types/Order";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import { isValidDomain } from "@shared/services/sales_rep_domain_validation";

  export let show = false;
  export let salesRep: SalesRep;
  export let order: SharedOrder = null;
  export let resend: boolean = false;
  export let successCallback: (updatedContact) => void = () => {};
  export let addRepToOrder: (salesRep: SalesRep) => void = () => {};
  export let removeRepFromOrder: (salesRep: SalesRep) => void = () => {};
  export let reloadOnSuccess = false;

  let fullName: string;
  let email: string;
  let mobilePhone: string;
  let disabled: boolean;
  let editingSalesRep: SalesRep;
  let suggestedSalesRep: SalesRep;
  let rendered = false;
  let disabledSave = false;

  $: errorMessage = { email: null, phone: null, name: null };
  $: {
    if (salesRep && !rendered) {
      disabled = salesRep.is_provi_user || salesRep.customer_service;
      if (salesRep.retailer_sales_rep_contact) {
        fullName = salesRep.retailer_sales_rep_contact.full_name;
        email = salesRep.retailer_sales_rep_contact.sanitized_email;
        mobilePhone = salesRep.retailer_sales_rep_contact.sanitized_phone;
      } else {
        fullName = salesRep.name;
        email = salesRep.contact_email;
        mobilePhone = salesRep.contact_phone;
      }
      rendered = true;
    }

    if (errorMessage.name || errorMessage.email || errorMessage.phone) {
      disabledSave = true;
    } else {
      disabledSave = false;
    }
  }

  const editSalesRep = async (
    retailerSalesRepContact?: RetailerSalesRepContact,
    confirm = false
  ) => {
    if (disabled) return;
    disabledSave = true;

    if (retailerSalesRepContact && retailerSalesRepContact.id) await editExistentContact(confirm);
    else await createContact(confirm);
  };

  // Edit Retailer Sales Rep from this modal or the verifiy modal
  const editExistentContact = async (confirm: boolean) => {
    try {
      const res = await HttpPromise({
        method: "PUT",
        url: `/api/retailer/retailer_sales_rep_contacts/${salesRep.retailer_sales_rep_contact?.id}`,
        data: {
          retailer_sales_rep_contact: {
            full_name: fullName,
            contact_phone: mobilePhone,
            contact_email: email,
            confirm_contact: confirm,
          },
          order_id: order?.id,
          resend: resend,
        },
      });
      handleSuccess(res.retailer_sales_rep_contact, res.retailer_contact_removed);
    } catch (errorRes) {
      disabledSave = false;
      await handleApiError(errorRes);
    }
  };

  // Create new Retailer Sales Rep
  const createContact = async (confirm: boolean) => {
    const res = await HttpPromise({
      method: "POST",
      url: "/api/retailer/retailer_sales_rep_contacts",
      data: {
        retailer_sales_rep_contact: {
          sales_rep_id: salesRep.id,
          full_name: fullName,
          contact_phone: mobilePhone,
          contact_email: email,
          confirm_contact: confirm,
        },
        order_id: order?.id,
        resend: resend,
      },
    }).catch(async err => {
      disabledSave = false;
      await handleApiError(err);
    });

    if (res) {
      handleSuccess(res, false);
    }
  };

  const handleApiError = async (errorRes: any) => {
    if (errorRes?.errors?.existent_sales_rep) {
      editingSalesRep = {
        ...salesRep,
        contact_phone: mobilePhone,
        contact_email: email,
        name: fullName,
      };
      suggestedSalesRep = errorRes.errors.existent_sales_rep;
      openVerifySalesRepModal();
    } else if (errorRes?.errors?.contact_email || errorRes?.errors?.contact_phone) {
      errorMessage.email = errorRes?.errors?.contact_email;
      errorMessage["phone"] = errorRes?.errors?.contact_phone;
    } else if (errorRes?.errors?.base) {
      Flash.now({ type: "error", header: "Error", message: errorRes.errors.base[0] });
    } else {
      Flash.now({ type: "error", header: "Error", message: errorRes?.errors?.base });
    }
  };

  const handleSuccess = (
    retailerSalesRepContactRes: RetailerSalesRepContact,
    contactRemoved: boolean
  ) => {
    const salesRepNewData = {
      ...salesRep,
      retailer_sales_rep_contact: contactRemoved ? null : retailerSalesRepContactRes,
      has_email_notification_errors: calculateEmailNotificationErrorsNewValue(),
      has_phone_notification_errors: calculatePhoneNotificationErrorsNewValue(),
    };
    DistributorsStore.updateDistributorSalesRep(
      salesRep.distributor_id,
      salesRep.id,
      salesRepNewData
    );

    successCallback(retailerSalesRepContactRes);
    disabledSave = false;
    Flash.now({ type: "success", message: successMessage(), header: "Success" });
    closeModal();
    if (reloadOnSuccess) window.location.reload();
  };

  const successMessage = () => {
    if (resend) {
      return "Distributor contact updated and order sent";
    } else {
      return "Distributor changes saved";
    }
  };

  const calculateEmailNotificationErrorsNewValue = (): boolean => {
    if (!salesRep.retailer_sales_rep_contact) {
      if (salesRep.contact_email !== email) return false;
      else return salesRep.has_email_notification_errors;
    } else {
      if (email !== salesRep?.retailer_sales_rep_contact?.contact_email) return false;
      else return salesRep.has_email_notification_errors;
    }
  };

  const calculatePhoneNotificationErrorsNewValue = (): boolean => {
    if (!salesRep.retailer_sales_rep_contact) {
      if (salesRep.contact_phone !== mobilePhone) return false;
      else return salesRep.has_phone_notification_errors;
    } else {
      if (mobilePhone !== salesRep?.retailer_sales_rep_contact?.contact_phone) return false;
      else return salesRep.has_phone_notification_errors;
    }
  };

  let showVerifySalesRepModal: boolean = false;
  const openVerifySalesRepModal = () => {
    showVerifySalesRepModal = true;
    show = false;
  };

  const showEditSalesRepModal = () => {
    showVerifySalesRepModal = false;
    show = true;
  };

  const closeModal = (clearErrors: boolean = true) => {
    salesRep = null;
    fullName = "";
    email = "";
    mobilePhone = "";
    show = false;
    rendered = false;
    if (clearErrors) {
      errorMessage = { email: null, phone: null, name: null };
    }
  };

  // hide error
  const onFocus = (event: any) => {
    if (event.target?.name === "sales-rep-modal_full_name") {
      nameFieldEventHandler(event.target.value);
      errorMessage.name = null;
    }
    if (event.target?.name === "sales-rep-modal_email") {
      emailFieldEventHandler(event.target.value);
      errorMessage.email = null;
    }
    if (event.detail?.target?.name === "mobile_phone") {
      phoneFieldEventHandler(event);
      errorMessage.phone = null;
    }
  };

  const onBlur = (event: any) => {
    if (event.target.name === "sales-rep-modal_full_name") {
      nameFieldEventHandler(event.target.value);
    }
    if (event.target.name === "sales-rep-modal_email") {
      emailFieldEventHandler(event.target.value);
    }
  };

  // validation in the back end will save the SR name if no name entered. Do we need this?
  const nameFieldEventHandler = (value: string) => {
    if (!value.length) {
      errorMessage.name = "You must enter a name";
    } else {
      errorMessage.name = null;
    }
  };

  // does not account for symbol before "@" - ex. ret@iler@mail.com
  const emailFieldEventHandler = (value: string) => {
    if (!value) return;

    let validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    let domainValidation = distributorDomainValidator(email);

    if (!validEmailRegex) {
      errorMessage.email = "You must enter a valid email address";
    } else if (!domainValidation.valid) {
      errorMessage.email = domainValidation.message;
    } else {
      errorMessage.email = null;
    }
  };

  const phoneFieldEventHandler = (e: any) => {
    let phoneInput = e.detail.target.value.replace(/\D/g, "");
    let isValid = phoneInput.length === 10 || phoneInput.length === 11;

    if (phoneInput.length && !isValid) {
      errorMessage.phone = "You must enter a valid phone number";
    } else {
      errorMessage.phone = null;
    }
  };

  const distributorDomainValidator = (value: string) => {
    let domain = value.split("@")[1];

    return {
      valid: isValidDomain(domain, salesRep.distributor_id) || value == "",
      message:
        "This email belongs to a different distributor. Select a different distributor or enter a new email.",
    };
  };

</script>

<style>
  .show-alert {
    padding: 1.5rem 4rem 0rem 1.5rem;
  }

</style>

<FizzStyles>
  <Modal
    closeOnBackgroundClick={true}
    bind:show
    size={"medium"}
    fizzModal={true}
    onClose={closeModal}
  >
    <div class="layout-header-footer">
      <header class="fizz-modal-header" class:show-alert={disabled} class:fizz-stack-8={disabled}>
        <h4 class="fizz-heading-4">Edit Contact</h4>
        <div class="fizz-modal-toggle fizz-modal-close" on:click={() => closeModal()}>
          <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="16" width="16" />
        </div>
      </header>
      <section class="fizz-modal-main fizz-text-medium fizz-flow-16" data-grid-area="main">
        {#if disabled}
          <div class="fizz-stack-16">
            <div class="fizz-alert-info">
              <p class="fizz-stack-4"><strong>This contact cannot be edited</strong></p>
              <p>
                Reach out to our <ChatWithSupportLink message={"support team"} bold={false} />
                if contact information needs to be updated
              </p>
            </div>
          </div>
        {/if}
        <div>
          <label class="fizz-label fizz-stack-4" for="sales-rep-modal_full_name">Full Name</label>
          <input
            class="fizz-input-text fizz-input fizz-full-width"
            type="text"
            id="sales-rep-modal_full_name"
            name="sales-rep-modal_full_name"
            bind:value={fullName}
            disabled={true}
            on:focus={onFocus}
            on:blur={onBlur}
          />
        </div>
        <div>
          <label class="fizz-label fizz-stack-4" for="sales-rep-modal_email">Email</label>
          <input
            class="fizz-input-text fizz-input fizz-full-width"
            class:fizz-input-error={errorMessage.email}
            type="email"
            id="sales-rep-modal_email"
            name="sales-rep-modal_email"
            bind:value={email}
            {disabled}
            on:focus={onFocus}
            on:blur={onBlur}
          />
          <small class="fizz-text-subdued">To send orders or contact rep via Provi Chat.</small>
          {#if errorMessage.email}
            <div class="fizz-flex-row-4">
              <Icon type="Error" height="16" width="16" />
              <p class="fizz-text-medium fizz-text-negative">{errorMessage.email}</p>
            </div>
          {/if}
        </div>
        <PhoneInput
          bind:value={mobilePhone}
          label={"Mobile Phone"}
          id="mobile_phone"
          extraClasses={"fizz-input"}
          description="To send orders via text."
          onBlur={phoneFieldEventHandler}
          {onFocus}
          {disabled}
          errorMessage={errorMessage.phone}
        />
        <div class="fizz-flex-row fizz-justify-end">
          <button class="fizz-button" on:click={() => closeModal()}>Cancel</button>
          {#if !disabled}
            <button
              class="fizz-button-secondary"
              disabled={disabledSave}
              on:click={() => editSalesRep(salesRep.retailer_sales_rep_contact)}>Save</button
            >
          {/if}
        </div>
      </section>
    </div>
  </Modal>
  <VerifySalesRepModal
    bind:show={showVerifySalesRepModal}
    {showEditSalesRepModal}
    {editingSalesRep}
    {suggestedSalesRep}
    {editSalesRep}
    {closeModal}
    {order}
    {removeRepFromOrder}
    {addRepToOrder}
    {reloadOnSuccess}
  />
</FizzStyles>
